import { defineMessages } from 'react-intl';

export const scope = 'app.containers.SendOffer';

export default defineMessages({
  // AXHE-4233 drawer step titles — the DrawerHeader is the single source of
  // truth for a step's name, so these render there and nowhere else (no more
  // in-panel `<h5>` per step).
  documentsStepHeading: {
    id: `${scope}.documentsStepHeading`,
    defaultMessage: 'Documents',
  },
  formStepHeading: {
    id: `${scope}.formStepHeading`,
    defaultMessage: 'Document details',
  },
  // AXHE-4181 — the document list that fronts the flow when the signing stage
  // holds more than one customizable document.
  documentsHint: {
    id: `${scope}.documentsHint`,
    defaultMessage:
      'Each document is filled in, previewed and sent on its own.',
  },
  documentsEmptyTitle: {
    id: `${scope}.documentsEmptyTitle`,
    defaultMessage: 'No documents to send',
  },
  documentsEmptyBody: {
    id: `${scope}.documentsEmptyBody`,
    defaultMessage:
      'This signing stage has no signature templates yet. Add one to the stage, then come back to send it.',
  },
  documentUpdated: {
    id: `${scope}.documentUpdated`,
    defaultMessage: 'Updated {date}',
  },
  backToDocuments: {
    id: `${scope}.backToDocuments`,
    defaultMessage: 'Back to documents',
  },
  documentStateNotStarted: {
    id: `${scope}.documentStateNotStarted`,
    defaultMessage: 'Not started',
  },
  documentStateDraft: {
    id: `${scope}.documentStateDraft`,
    defaultMessage: 'In progress',
  },
  documentStatePendingApproval: {
    id: `${scope}.documentStatePendingApproval`,
    defaultMessage: 'Awaiting approval',
  },
  documentStateApproved: {
    id: `${scope}.documentStateApproved`,
    defaultMessage: 'Approved',
  },
  documentStateSent: {
    id: `${scope}.documentStateSent`,
    defaultMessage: 'Sent',
  },
  documentStateCompleted: {
    id: `${scope}.documentStateCompleted`,
    defaultMessage: 'Signed',
  },
  documentStateSendFailed: {
    id: `${scope}.documentStateSendFailed`,
    defaultMessage: 'Send failed',
  },
  documentStateCanceled: {
    id: `${scope}.documentStateCanceled`,
    defaultMessage: 'Canceled',
  },
  documentStateUnknown: {
    id: `${scope}.documentStateUnknown`,
    defaultMessage: 'Status unavailable',
  },
  documentActionStart: {
    id: `${scope}.documentActionStart`,
    defaultMessage: 'Start',
  },
  documentActionStartLabel: {
    id: `${scope}.documentActionStartLabel`,
    defaultMessage: 'Start {title}',
  },
  documentActionContinue: {
    id: `${scope}.documentActionContinue`,
    defaultMessage: 'Continue',
  },
  documentActionContinueLabel: {
    id: `${scope}.documentActionContinueLabel`,
    defaultMessage: 'Continue {title}',
  },
  documentActionOpen: {
    id: `${scope}.documentActionOpen`,
    defaultMessage: 'Open',
  },
  documentActionOpenLabel: {
    id: `${scope}.documentActionOpenLabel`,
    defaultMessage: 'Open {title}',
  },
  // The visible "View" itself comes from globalMessages.view — a generic verb
  // belongs in the shared catalog so it is translated once.
  documentActionViewLabel: {
    id: `${scope}.documentActionViewLabel`,
    defaultMessage: 'View {title}',
    description:
      'Accessible name for the sent-document row action that opens the delivered offer read-only (AXHE-4682); {title} is the document name',
  },
  untitledDocument: {
    id: `${scope}.untitledDocument`,
    defaultMessage: 'Untitled document',
  },
  // "Conditional blocks", not "Section details" (AXHE-4405 §6): the recruiter
  // answers these to resolve what the template's author configured, and the
  // author configured them under that name — it is the editor rail's tab, and the
  // templates table's column (OfferLetterTemplates/messages.ts).
  //
  // Sentence case, unlike the ticket's "Conditional Blocks": both existing
  // surfaces ship "Conditional blocks", and sentence case is the house rule for
  // headings. Three surfaces, one name, one casing.
  //
  // Both ids are edited in place rather than re-minted. The convention in this
  // file is the opposite (see documentApprovalRequired below) — a translated id's
  // English must not change, or every non-English catalog serves the superseded
  // string until Smartling catches up. It does not apply here: neither key is in
  // any of the 77 catalogs except en.json, so there is no translation to supersede
  // and a fresh id would only orphan itself.
  conditionFieldsHeading: {
    id: `${scope}.conditionFieldsHeading`,
    defaultMessage: 'Conditional blocks',
  },
  // Follows the heading: "sections" would name something the recruiter can no
  // longer see under a heading that says "blocks".
  conditionFieldsHint: {
    id: `${scope}.conditionFieldsHint`,
    defaultMessage: 'These answers decide which blocks appear in the letter.',
  },
  // The empty answer on any control with a closed value set (AXHE-4661). Named
  // for its EFFECT rather than its state: an unanswered field falls back to the
  // applicant's own data (AXHE-4001), so "No selection" promised a blank the
  // letter does not carry — a recruiter who cleared a prefilled control read an
  // empty box while the section it drives stayed in the document.
  applicantDataOption: {
    id: `${scope}.applicantDataOption`,
    defaultMessage: 'Use applicant data',
    description:
      "The empty option in a Send Offer dropdown or radio group. Choosing it leaves the field unanswered, and an unanswered field takes the applicant's own value. Serves two grammatical roles: an option label beside the answers in a radio group, and the closed dropdown's display value when nothing is answered",
  },
  currencyHint: {
    id: `${scope}.currencyHint`,
    defaultMessage: 'Rendered as a number and in words on the letter',
  },
  currencyPlainHint: {
    id: `${scope}.currencyPlainHint`,
    defaultMessage: 'Rendered as a formatted number on the letter',
  },
  // AXHE-4543. Shown while the field is blank/untouched (clearing it counts
  // as untouched too — see `effectiveFormulaValue`) and the formula resolved
  // to a value. Lower priority than the currency hints above: a computed
  // CURRENCY field still needs to promise (or not) the written-out amount,
  // which this would otherwise crowd out.
  //
  // Third-person/passive to match every sibling hint on this same panel
  // (`currencyHint`, `currencyPlainHint`, `conditionFieldsHint`) — none of
  // them address "you"; this shouldn't be the one that does.
  formulaCalculatedHint: {
    id: `${scope}.formulaCalculatedHint`,
    defaultMessage: "Calculated automatically from another field's answer",
    description:
      "Helper text under a number field whose value was computed from another field's answer rather than typed or pre-filled from Fountain",
  },
  // The same gate as `formulaCalculatedHint` but for when the formula could
  // NOT resolve — the reference is blank, unanswered, or ambiguous — so the
  // field renders blank instead of a silently missing number with no
  // explanation. Same register as its sibling above.
  formulaUnresolvedHint: {
    id: `${scope}.formulaUnresolvedHint`,
    defaultMessage: 'Not yet calculated — the field it depends on has no answer',
    description:
      'Helper text under a number field whose formula could not resolve to a value, e.g. because the referenced field is still blank',
  },
  requiredMissing: {
    id: `${scope}.requiredMissing`,
    defaultMessage: 'Required to generate the letter',
  },
  // This id — and eight others below (documentDetailsLoadError,
  // previewDocumentCta, documentPreviewTitle, renderingDocument,
  // downloadDocument, sendForSigning, uploadCustomDocument,
  // documentReplaced) — was minted FRESH when AXHE-4233
  // changed its English, replacing an already-translated id. Editing a
  // translated id's defaultMessage in place leaves every non-English catalog
  // serving the superseded translation until Smartling re-translates; a fresh
  // id has no catalog entries, so every locale falls back to the new English
  // (PR #3573 review).
  documentApprovalRequired: {
    id: `${scope}.documentApprovalRequired`,
    defaultMessage: 'This document requires approval before it can be sent',
  },
  cannotSend: {
    id: `${scope}.cannotSend`,
    defaultMessage:
      '{count, plural, one {# field still needs a value} other {# fields still need values}} before this can be sent.',
  },
  // AXHE-4441. A `[SIGNATURE_<ROLE>]` placeholder for a role that is not on the
  // template's signer roster blocks the send, and there is nothing on the panel
  // to flag: signature fields are deliberately not recruiter inputs, so the form
  // renders no control for one. It read as "1 field still needs a value" over a
  // panel with every field filled in and nothing marked.
  //
  // So it says what is actually wrong, names the role, and points at the two
  // places the fix lives — neither of which is a field on this form. "Ask an
  // admin" because it genuinely is someone else's change: no value the recruiter
  // enters resolves a dangling signer.
  //
  // One line per unresolvable role rather than one line listing them: two is
  // rare, and a plural-aware sentence that has to name a list AND stay
  // actionable is worse for the reader and for whoever translates it.
  signerNotOnTemplate: {
    id: `${scope}.signerNotOnTemplate`,
    defaultMessage:
      'The document has a signature for {role}, but the template has no signer for that role. Ask an admin to add the signer to the template, or remove the signature from the document.',
    description:
      'Shown beside the send CTA when the document places a signature for a signer role the offer letter template does not have, which blocks sending and cannot be fixed on this form',
  },
  documentDetailsLoadError: {
    id: `${scope}.documentDetailsLoadError`,
    defaultMessage: 'Could not load the document details.',
  },
  // AXHE-4233: the readiness check ("Preview document") can fail in transit
  // (network/500), distinct from a readiness result that says the document
  // isn't ready yet — that case renders inline field errors instead, see
  // `cannotSend`. Reuses the same `actionError` display slot as the
  // create/update failure message below it in index.tsx.
  checkError: {
    id: `${scope}.checkError`,
    defaultMessage: 'Could not check the document. Please try again.',
  },
  // Fallbacks ONLY (AXHE-4234). When the server said why the request failed,
  // the recruiter is shown that message instead — these two cover the cases
  // where there is genuinely nothing to relay: a network failure, or a
  // response that isn't the JSON error envelope.
  // Offer -> Document wording (AXHE-4233) applied here once AXHE-4234 merged —
  // these were left verbatim while that PR was open to keep its rebase clean.
  previewError: {
    id: `${scope}.previewError`,
    defaultMessage: 'Could not generate the document preview. Please try again.',
    description:
      'Error shown when building a draft job offer document for review fails and the reason is unknown; retrying is worth attempting',
  },
  sendError: {
    id: `${scope}.sendError`,
    defaultMessage: 'Could not send the document. Please try again.',
    description:
      'Error shown when delivering a job offer to the candidate for signature fails and the reason is unknown; retrying is worth attempting',
  },
  previewDocumentCta: {
    id: `${scope}.previewDocumentCta`,
    defaultMessage: 'Preview document',
  },
  // Doubles as the preview step's DrawerHeader title and the preview iframe's
  // `title` attribute.
  documentPreviewTitle: {
    id: `${scope}.documentPreviewTitle`,
    defaultMessage: 'Document preview',
  },
  renderingDocument: {
    id: `${scope}.renderingDocument`,
    defaultMessage: 'Rendering the document',
  },
  approversHeading: {
    id: `${scope}.approversHeading`,
    defaultMessage: 'Approvers',
  },
  downloadDocument: {
    id: `${scope}.downloadDocument`,
    defaultMessage: 'Download document',
  },
  // AXHE-4406 §2 / Notion §12 — the group heading over Download and Upload,
  // which moved out of the footer to the left of the rendered document.
  // Sentence case, unlike Notion's "Document Actions": FDS is sentence case
  // throughout.
  documentActionsHeading: {
    id: `${scope}.documentActionsHeading`,
    defaultMessage: 'Document actions',
    description:
      'Heading over the Download document / Upload custom document buttons beside a job offer preview. Names a group of tools that act on the document itself, as opposed to sending it',
  },
  // AXHE-4406 §4 removed the local `back` ("Back to edit"): the footer control
  // is labelled plain "Back" now and takes the word from `globalMessages.back`,
  // which is already translated in every locale. Relabelling the local id would
  // have left 73 catalogs serving "Back to edit" until Smartling caught up.
  sendForApproval: {
    id: `${scope}.sendForApproval`,
    defaultMessage: 'Send for approval',
  },
  sendForSigning: {
    id: `${scope}.sendForSigning`,
    defaultMessage: 'Send for signing',
  },
  submitted: {
    id: `${scope}.submitted`,
    defaultMessage: 'Submitted. Status: {status}.',
  },
  sendingToCandidate: {
    id: `${scope}.sendingToCandidate`,
    defaultMessage: 'Sending to candidate',
  },
  sentToCandidate: {
    id: `${scope}.sentToCandidate`,
    defaultMessage: 'Document sent to the candidate',
  },
  sendDelayed: {
    id: `${scope}.sendDelayed`,
    defaultMessage:
      'Still sending — this is taking longer than expected (status: {status}). Check back in a few minutes; there is no need to send it again.',
  },
  sendCanceled: {
    id: `${scope}.sendCanceled`,
    defaultMessage:
      "This document was canceled, so it won't be sent. Go back to edit to start a new document.",
  },
  sendFailed: {
    id: `${scope}.sendFailed`,
    defaultMessage:
      "This document couldn't be sent — the candidate did not receive it. Go back to edit, review the document and the signing stage's signer setup, then send it again.",
  },
  uploadCustomDocument: {
    id: `${scope}.uploadCustomDocument`,
    defaultMessage: 'Upload custom document',
  },
  documentReplaced: {
    id: `${scope}.documentReplaced`,
    defaultMessage: 'Document replaced with your uploaded PDF.',
  },
  uploadError: {
    id: `${scope}.uploadError`,
    defaultMessage: 'We could not upload that document. Please try again.',
  },
  // AXHE-4285 — the Upload Override state, and the way out of it. Replaces the
  // "Uploaded PDF" chip that used to sit in the preview header: this says the
  // same thing, names the template the upload displaced, and carries the
  // control that undoes it, so keeping both would have been two labels for one
  // state (requirements doc §13).
  customDocumentActive: {
    id: `${scope}.customDocumentActive`,
    defaultMessage: 'Custom document active',
    description:
      'Status heading on a card in the job-offer preview, shown when the recruiter has replaced the generated offer letter with a PDF of their own. "Active" means this uploaded file, not the template, is what the applicant will receive and sign',
  },
  // AXHE-4400. Not a bare noun swap: the old subject was "This offer", and an
  // offer can loosely be said to "send" something where a document cannot. Naming
  // the recipient instead keeps the sentence grammatical AND states the actual
  // consequence, which is what the card is for.
  customDocumentReplacesTemplate: {
    id: `${scope}.customDocumentReplacesTemplate`,
    defaultMessage:
      'The applicant receives the PDF you uploaded instead of {template}.',
    description:
      'Body of the "Custom document active" card. {template} is the name of the signature template the upload displaced, e.g. "Full-time Offer" — or the customDocumentTemplateFallback phrase when the name is unknown, so it must read naturally mid-sentence',
  },
  // Stands in for the template name when the form payload carried no title —
  // the sentence above has to read as a sentence either way.
  customDocumentTemplateFallback: {
    id: `${scope}.customDocumentTemplateFallback`,
    defaultMessage: 'the template document',
    description:
      'Substituted for {template} in customDocumentReplacesTemplate and revertConfirmBody when the template name is unavailable. Translate as a generic noun phrase that reads naturally mid-sentence in place of a document name',
  },
  revertToTemplate: {
    id: `${scope}.revertToTemplate`,
    defaultMessage: 'Revert to template',
    description:
      'Button that discards the PDF the recruiter uploaded and rebuilds the job offer from its original template. Imperative verb phrase — an action the recruiter takes, not a label for a state',
  },
  revertConfirmTitle: {
    id: `${scope}.revertConfirmTitle`,
    defaultMessage: 'Revert to the template document?',
    description:
      'Title of the confirmation dialog shown before discarding the recruiter\'s uploaded PDF. A question asking them to confirm the action',
  },
  revertConfirmBody: {
    id: `${scope}.revertConfirmBody`,
    defaultMessage:
      "The PDF you uploaded will be deleted and the document rebuilt from {template}. Any signature fields you placed on it will be removed too. This can't be undone.",
    description:
      'Body of that confirmation dialog, spelling out the consequences. {template} is the signature template name. "Signature fields" are the boxes the recruiter drew on the PDF marking where each signer signs',
  },
  documentReverted: {
    id: `${scope}.documentReverted`,
    defaultMessage: 'Document reverted to the template.',
    description:
      'Brief success notification after the uploaded PDF was discarded and the offer regenerated from its template. Past tense',
  },
  revertError: {
    id: `${scope}.revertError`,
    defaultMessage: 'We could not revert the document. Please try again.',
    description:
      'Fallback error shown when discarding the uploaded PDF fails and the server gave no reason of its own; retrying is worth attempting',
  },
  signatureFieldsNone: {
    id: `${scope}.signatureFieldsNone`,
    defaultMessage:
      'No signature fields on this PDF yet — the candidate would receive nothing to sign.',
  },
  signatureFieldsCount: {
    id: `${scope}.signatureFieldsCount`,
    defaultMessage:
      '{count, plural, one {# signature field} other {# signature fields}} on this PDF',
  },
  // AXHE-4233 dirty-close guard — copies the DataValidationModal pattern
  // (local action/cancel copy + the shared `globalMessages.unsavedChanges`
  // title and `globalMessages.unsavedProgress` body, both used directly from
  // index.tsx rather than duplicated here).
  discardChanges: {
    id: `${scope}.discardChanges`,
    defaultMessage: 'Discard changes',
  },
  keepEditing: {
    id: `${scope}.keepEditing`,
    defaultMessage: 'Keep editing',
  },
  // Cancel offer (AXHE-4682). The confirmation names every consequence for the
  // state the offer is actually in, and ends with the irreversibility sentence
  // — the revert confirmation's pattern.
  cancelOffer: {
    id: `${scope}.cancelOffer`,
    defaultMessage: 'Cancel offer',
    description:
      'Destructive action in the preview rail and the confirmation dialog: stops the offer outright (AXHE-4682)',
  },
  keepOffer: {
    id: `${scope}.keepOffer`,
    defaultMessage: 'Keep offer',
    description:
      'Dismiss button of the cancel-offer confirmation — "Cancel" would collide with the action being confirmed',
  },
  cancelOfferConfirmTitle: {
    id: `${scope}.cancelOfferConfirmTitle`,
    defaultMessage: 'Cancel this offer?',
    description: 'Title of the cancel-offer confirmation dialog; states the consequence as a question',
  },
  cancelOfferConfirmBodySent: {
    id: `${scope}.cancelOfferConfirmBodySent`,
    defaultMessage:
      "The applicant will no longer be able to open or sign this document. This can't be undone.",
    description:
      'Cancel-offer confirmation body for a sent offer: the signing request is voided at the vendor',
  },
  cancelOfferConfirmBodyPending: {
    id: `${scope}.cancelOfferConfirmBodyPending`,
    defaultMessage:
      "The open approval request will be withdrawn and the offer canceled. This can't be undone.",
    description:
      'Cancel-offer confirmation body while the offer is awaiting approval: approvers are asked to decide nothing',
  },
  cancelOfferConfirmBodyOther: {
    id: `${scope}.cancelOfferConfirmBodyOther`,
    defaultMessage:
      "The offer will be canceled and won't be sent to the applicant. This can't be undone.",
    description:
      'Cancel-offer confirmation body for an approved or send-failed offer: nothing has reached the applicant yet',
  },
  offerCanceled: {
    id: `${scope}.offerCanceled`,
    defaultMessage: 'Offer canceled.',
    description: 'Brief success notification after an offer was canceled. Past tense',
  },
  cancelOfferError: {
    id: `${scope}.cancelOfferError`,
    defaultMessage: 'We could not cancel the offer. Please try again.',
    description:
      'Fallback error when the cancel fails and the server gave no reason of its own; retrying is worth attempting',
  },
  viewOfferError: {
    id: `${scope}.viewOfferError`,
    defaultMessage: 'We could not open this document. Please try again.',
    description:
      'Error when loading a sent offer for the read-only view fails; the recruiter stays on the document list',
  },
  // Edit and re-send a sent offer (AXHE-4425). "Edit offer" rather than the
  // shared "Edit": in the rail it sits under "Download document" and above
  // "Cancel offer", where a bare verb names no object.
  editOffer: {
    id: `${scope}.editOffer`,
    defaultMessage: 'Edit offer',
    description:
      'Action in the preview rail of a sent offer: opens a revision pre-filled with the offer values, for a negotiated change (AXHE-4425)',
  },
  reSendToApplicant: {
    id: `${scope}.reSendToApplicant`,
    defaultMessage: 'Re-send to applicant',
    description:
      'Send CTA when the offer being sent replaces one the applicant already has. Replaces "Send for signing" for a revision',
  },
  reviseOfferError: {
    id: `${scope}.reviseOfferError`,
    defaultMessage: 'We could not open this offer for editing. Please try again.',
    description:
      'Fallback error when opening a revision fails and the server gave no reason of its own; retrying is worth attempting',
  },
  // The card naming the document the applicant is holding while a revision is
  // being edited. "Currently with the applicant" states what it is; the row
  // under it dates it, the same way a version-history row does.
  liveVersionHeading: {
    id: `${scope}.liveVersionHeading`,
    defaultMessage: 'Currently with the applicant',
    description:
      'Heading of the card in the preview rail showing the offer document the applicant holds right now, while the recruiter edits a replacement for it',
  },
  versionHistoryHeading: {
    id: `${scope}.versionHistoryHeading`,
    defaultMessage: 'Version history',
    description:
      'Heading over the list of earlier offer documents that have been replaced. Past versions only',
  },
  versionSentBy: {
    id: `${scope}.versionSentBy`,
    defaultMessage: 'Sent {date} by {sender}',
    description:
      'Labels one version of an offer document. {date} is a short date and time — two versions can be sent the same day — and {sender} the name of the user who sent it',
  },
  versionSent: {
    id: `${scope}.versionSent`,
    defaultMessage: 'Sent {date}',
    description:
      'Labels one version of an offer document whose sender is no longer recorded — the user was removed, or the offer predates the column',
  },
  versionUndated: {
    id: `${scope}.versionUndated`,
    defaultMessage: 'Earlier version',
    description:
      'Labels one version of an offer document with no recorded send date. Offers sent before the send date was stored, and whose signature request is gone, have nothing to date them by',
  },
  downloadVersion: {
    id: `${scope}.downloadVersion`,
    defaultMessage: 'Download the version sent {date}',
    description:
      'Accessible name of a version row Download button, which reads only "Download" on screen. {date} carries a time so two versions sent the same day do not share one name',
  },
  downloadUndatedVersion: {
    id: `${scope}.downloadUndatedVersion`,
    defaultMessage: 'Download an earlier version of this document',
    description:
      'Accessible name of a version row Download button when the version carries no send date',
  },
  // Discarding a revision (AXHE-4425). Nothing the applicant holds is touched,
  // so the confirmation says so — the loss is the recruiter's own edit.
  discardRevisionTitle: {
    id: `${scope}.discardRevisionTitle`,
    defaultMessage: 'Discard this edit?',
    description:
      'Title of the confirmation shown when leaving an unsent offer revision; states the consequence as a question',
  },
  discardRevisionBody: {
    id: `${scope}.discardRevisionBody`,
    defaultMessage:
      'Your changes are not saved. The applicant keeps the offer you already sent.',
    description:
      'Body of the discard-revision confirmation: names what is lost and what is not',
  },
  discardRevision: {
    id: `${scope}.discardRevision`,
    defaultMessage: 'Discard edit',
    description: 'Action button of the discard-revision confirmation',
  },
  discardRevisionError: {
    id: `${scope}.discardRevisionError`,
    defaultMessage: 'We could not discard this edit. Please try again.',
    description:
      'Fallback error when discarding a revision fails and the server gave no reason of its own',
  },
});
